import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios, { apiAxiosUpload } from "helpers/apiAxios";
import { objectSerializer } from "helpers/utils";
import { toast } from "react-toastify";
import { addRejectedMatcher, handleAsyncThunkError } from "redux/reduxHelpers";

const engineeringAdviceUrl = (projectId, buildingId, adviceId) => (
  adviceId
    ? `/crm/projects/${projectId}/buildings/${buildingId}/engineering_advices/${adviceId}`
    : `/crm/projects/${projectId}/buildings/${buildingId}/engineering_advices`
);

export const getBuildingEngineeringAdvices = createAsyncThunk(
  "EngineeringAdvice/getBuildingEngineeringAdvices",
  async ({ projectId, buildingId, params }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(engineeringAdviceUrl(projectId, buildingId), { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get engineering advices'));
    }
  }
);

export const createEngineeringAdvice = createAsyncThunk(
  "EngineeringAdvice/createEngineeringAdvice",
  async ({ projectId, buildingId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(engineeringAdviceUrl(projectId, buildingId), data);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create engineering advice'));
    }
  }
);

export const updateEngineeringAdvice = createAsyncThunk(
  "EngineeringAdvice/updateEngineeringAdvice",
  async ({ projectId, buildingId, adviceId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(engineeringAdviceUrl(projectId, buildingId, adviceId), data);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update engineering advice'));
    }
  }
);

export const deleteEngineeringAdvice = createAsyncThunk(
  "EngineeringAdvice/deleteEngineeringAdvice",
  async ({ projectId, buildingId, adviceId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(engineeringAdviceUrl(projectId, buildingId, adviceId));
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete engineering advice'));
    }
  }
);

// Compose pre-fill for the send dialog: {to: [{name, email}], cc: [...], subject, content}.
export const getEngineeringAdviceMailDefaults = async (projectId, buildingId, adviceId, { target, kind }) => {
  const res = await apiAxios.get(`${engineeringAdviceUrl(projectId, buildingId, adviceId)}/mail_defaults`, {
    params: { target, kind },
  });
  return res.data;
};

// One thunk for every workflow email — action is the backend endpoint:
// send_to_csd | send_to_eng | eng_reply.
// `data` may be a FormData (composed email with file attachments).
export const sendEngineeringAdviceEmail = createAsyncThunk(
  "EngineeringAdvice/sendEngineeringAdviceEmail",
  async ({ projectId, buildingId, adviceId, action, data }, { rejectWithValue }) => {
    try {
      const url = `${engineeringAdviceUrl(projectId, buildingId, adviceId)}/${action}`;
      const client = data instanceof FormData ? apiAxiosUpload : apiAxios;
      const response = await client.post(url, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to send engineering advice email'));
    }
  }
);

// Stops a sheet before it closes; the record (and its correspondence) is kept.
export const cancelEngineeringAdvice = createAsyncThunk(
  "EngineeringAdvice/cancelEngineeringAdvice",
  async ({ projectId, buildingId, adviceId, reason }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(
        `${engineeringAdviceUrl(projectId, buildingId, adviceId)}/cancel`,
        { reason }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to cancel engineering advice'));
    }
  }
);

// Sheet attachments (Sales) — separate from the per-email attachments.
export const uploadEngineeringAdviceDocuments = createAsyncThunk(
  "EngineeringAdvice/uploadEngineeringAdviceDocuments",
  async ({ projectId, buildingId, adviceId, files }, { rejectWithValue }) => {
    try {
      const form = new FormData();
      files.forEach(file => form.append('documents[]', file));
      const response = await apiAxiosUpload.post(
        `${engineeringAdviceUrl(projectId, buildingId, adviceId)}/upload_document`,
        form
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to upload documents'));
    }
  }
);

export const removeEngineeringAdviceDocument = createAsyncThunk(
  "EngineeringAdvice/removeEngineeringAdviceDocument",
  async ({ projectId, buildingId, adviceId, documentId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(
        `${engineeringAdviceUrl(projectId, buildingId, adviceId)}/remove_document`,
        { document_id: documentId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to remove document'));
    }
  }
);

// Plain async fn (not a thunk) — opens the server-rendered PDF in a new tab,
// falling back to a download when the popup is blocked. Long timeout: default
// apiAxios timeout is 30s and wkhtmltopdf can exceed it.
export const openEngineeringAdvicePdf = async (projectId, buildingId, advice) => {
  const res = await apiAxios.get(`${engineeringAdviceUrl(projectId, buildingId, advice.id)}/print`, {
    responseType: "blob",
    timeout: 120000,
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/pdf" }));
  const win = window.open(url, "_blank");
  if (!win) {
    const cd = res.headers?.["content-disposition"] || "";
    const match = cd.match(/filename="?([^"]+)"?/);
    const a = document.createElement("a");
    a.href = url;
    a.download = match ? match[1] : `engineering-advice-${advice.advice_no}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// Per-building keyed state, mirroring crm_change_order.buildings.
const upsertBuilding = (state, buildingId, rows, totalRows) => {
  state.buildings[buildingId] = {
    id: buildingId,
    data: objectSerializer(rows),
    totalRows,
    loading: false,
  };
};

const engineeringAdviceSlice = createSlice({
  name: "crm_engineering_advice",
  initialState: {
    buildings: {},
    loading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getBuildingEngineeringAdvices.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBuildingEngineeringAdvices.fulfilled, (state, action) => {
        state.loading = false;
        const { buildingId } = action.meta.arg;
        upsertBuilding(state, buildingId, action.payload.rows, action.payload.total_rows);
      })
      .addCase(createEngineeringAdvice.fulfilled, (state, action) => {
        if (action.payload?.message) toast.success(action.payload.message);
      })
      .addCase(updateEngineeringAdvice.fulfilled, (state, action) => {
        if (action.payload?.message) toast.success(action.payload.message);
      })
      .addCase(deleteEngineeringAdvice.fulfilled, (state, action) => {
        if (action.payload?.message) toast.success(action.payload.message);
      })
      .addCase(sendEngineeringAdviceEmail.fulfilled, (state, action) => {
        if (action.payload?.message) toast.success(action.payload.message);
      })
      .addCase(cancelEngineeringAdvice.fulfilled, (state, action) => {
        if (action.payload?.message) toast.success(action.payload.message);
      });

    addRejectedMatcher(builder, "EngineeringAdvice");
  },
});

export default engineeringAdviceSlice.reducer;
